import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export function useProjectAccess(projectId) {
    const { user } = useAuth();
    const [access, setAccess] = useState(() => {
        // Try to load cached access from sessionStorage for instant display
        if (!user?.uid || !projectId) {
            return {
                isLoading: true,
                hasAccess: false,
                isAdmin: false,
                role: null,
                memberCount: 0,
                members: {}
            };
        }
        try {
            const cacheKey = `projectAccess_${projectId}_${user.uid}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed.hasAccess) {
                    return { ...parsed, isLoading: false };
                }
            }
        } catch {
            // Ignore cache errors
        }
        return {
            isLoading: true,
            hasAccess: false,
            isAdmin: false,
            role: null,
            memberCount: 0,
            members: {}
        };
    });

    useEffect(() => {
        async function checkAccess() {
            if (!user || !projectId) {
                setAccess(prev => ({ ...prev, isLoading: false, hasAccess: false }));
                return;
            }

            try {
                const projectDoc = await getDoc(doc(db, 'projects', projectId));

                if (!projectDoc.exists()) {
                    setAccess(prev => ({ ...prev, isLoading: false, hasAccess: false }));
                    return;
                }

                const projectData = projectDoc.data();
                const memberData = projectData.members?.[user.uid];

                // Check workspace access if project belongs to a workspace
                let workspaceAccess = null;
                if (projectData.workspaceId) {
                    const workspaceDoc = await getDoc(doc(db, 'workspaces', projectData.workspaceId));
                    if (workspaceDoc.exists()) {
                        const workspaceData = workspaceDoc.data();
                        workspaceAccess = workspaceData.members?.[user.uid];
                    }
                }

                const newAccess = {
                    isLoading: false,
                    // User has access if they have direct project access OR workspace access
                    hasAccess: !!memberData || !!workspaceAccess,
                    // User is admin if they're project admin OR workspace admin
                    isAdmin: memberData?.role === 'admin' || workspaceAccess?.role === 'admin',
                    // Prefer project role over workspace role
                    role: memberData?.role || workspaceAccess?.role || null,
                    memberCount: Object.keys(projectData.members || {}).length,
                    members: projectData.members || {}
                };

                setAccess(newAccess);

                // Cache access in sessionStorage for faster subsequent loads
                try {
                    const cacheKey = `projectAccess_${projectId}_${user.uid}`;
                    sessionStorage.setItem(cacheKey, JSON.stringify(newAccess));
                } catch {
                    // Ignore cache write errors
                }
            } catch (error) {
                console.error('Error checking project access:', error);
                setAccess(prev => ({ ...prev, isLoading: false, hasAccess: false }));
            }
        }

        checkAccess();
    }, [projectId, user]);

    return access;
}